import React from 'react';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, Typography, IconButton, Grid, Paper, List, ListItem, ListItemText, ListItemSecondaryAction } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import DeleteIcon from '@mui/icons-material/Delete';
import formatDate from 'date-fns/format';
import { parseISO, isSameDay, isAfter, isBefore, startOfDay } from 'date-fns';

export const UnavailabilityCalendar = ({ 
  selectedDates = [], 
  onChange,
  seasonStart = "2024-04-01", 
  seasonEnd = "2024-08-31",
  frozenFrom = null,
  frozenUntil = null
}) => {
  const isDateFrozen = (date) => {
    if (!frozenFrom) return false;
    const d = typeof date === 'string' ? parseISO(date) : date;
    const from = startOfDay(frozenFrom);
    const until = frozenUntil ? startOfDay(frozenUntil) : parseISO(seasonEnd);
    return d >= from && d <= until;
  };

  const handleDateChange = (newDate) => {
    if (!newDate) return;
    if (isDateFrozen(newDate)) return;
    
    const formattedDate = formatDate(newDate, 'yyyy-MM-dd');
    if (selectedDates.includes(formattedDate)) {
      onChange(selectedDates.filter(date => date !== formattedDate));
    } else {
      onChange([...selectedDates, formattedDate].sort());
    }
  };

  const handleDelete = (dateToDelete) => {
    if (isDateFrozen(dateToDelete)) return;
    onChange(selectedDates.filter(date => date !== dateToDelete));
  };

  const renderDates = () => {
    const today = startOfDay(new Date());
    const upcomingDates = selectedDates.filter(date => {
      const dateObj = parseISO(date);
      return !isBefore(dateObj, today);
    });

    if (!upcomingDates || upcomingDates.length === 0) {
      return (
        <Typography color="textSecondary" variant="body2" sx={{ p: 2 }}>
          No upcoming dates selected
        </Typography>
      );
    }

    return (
      <List dense sx={{ maxHeight: '200px', overflowY: 'auto' }}>
        {upcomingDates.map((date) => {
          const frozen = isDateFrozen(date);
          return (
            <ListItem key={date} sx={{ py: 0, opacity: frozen ? 0.5 : 1 }}>
              <ListItemText 
                primary={
                  <span>
                    {formatDate(parseISO(date), 'EEE, d MMM')}
                    {frozen && <span style={{ fontSize: '0.7em', color: '#e65100', marginLeft: 6 }}>locked</span>}
                  </span>
                }
              />
              {!frozen && (
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    aria-label="delete" 
                    size="small"
                    onClick={() => handleDelete(date)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          );
        })}
      </List>
    );
  };

  const CustomDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    
    const start = parseISO(seasonStart);
    const end = parseISO(seasonEnd);
    
    const isOutOfSeason = isBefore(day, start) || isAfter(day, end);
    const frozen = isDateFrozen(day);
    const isDisabled = isOutOfSeason || frozen;
    const isSelected = selectedDates.some(date => isSameDay(parseISO(date), day));

    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={isSelected}
        disabled={isDisabled}
        sx={frozen && !isSelected ? {
          backgroundColor: '#f5f5f5',
          color: '#bbb',
          '&.Mui-disabled': { color: '#bbb' }
        } : frozen && isSelected ? {
          backgroundColor: '#e0e0e0 !important',
          color: '#888 !important',
        } : {}}
      />
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            defaultValue={parseISO(seasonStart)}
            onChange={handleDateChange}
            slots={{
              day: CustomDay
            }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12} md={5}>
        <Paper 
          sx={{ 
            bgcolor: 'grey.50',
            height: { xs: 'auto', md: '320px' },
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography variant="subtitle1" sx={{ p: 2, pb: 1 }}>
            Upcoming Dates ({selectedDates.filter(date => !isBefore(parseISO(date), startOfDay(new Date()))).length})
          </Typography>
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {renderDates()}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};